import { valibotResolver } from '@hookform/resolvers/valibot';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  maxLength,
  minLength,
  object,
  pipe,
  string,
  type InferInput as ValibotInput,
} from 'valibot';
import { useCreateInternalRequest } from '@/api/internal-requests/queries';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const FormSchema = object({
  name: pipe(
    string(),
    minLength(1, 'Please enter feature name'),
    maxLength(100, 'Name cannot exceed 100 characters'),
  ),
  description: pipe(
    string(),
    minLength(10, 'Please enter feature description (At least 10 characters)'),
    maxLength(1000, 'Description cannot exceed 1000 characters'),
  ),
});

type FormDataType = ValibotInput<typeof FormSchema>;

export const SuggestFeatureForm = () => {
  const mutation = useCreateInternalRequest();

  const form = useForm<FormDataType>({
    resolver: valibotResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const onSubmit = (data: FormDataType) => {
    return mutation.mutateAsync(data, {
      onSuccess: () => {
        form.reset();
        toast.success('Feature request submitted successfully! Thank you for your suggestion.');
      },
      onError: () => {
        toast.error('Failed to submit feature request. Please try again.');
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 justify-end">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="What feature do you want?" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the feature in detail. What problem does it solve? How would it work?"
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          className="w-full sm:w-auto"
        >
          {form.formState.isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </form>
    </Form>
  );
};
